import {
	AlertCircle,
	FileText,
	Globe,
	Loader2,
	Smartphone,
	Users,
	X,
} from "lucide-react";
import React, { useEffect, useState } from "react";

const TargetingModal = ({ isOpen, onClose, packageData }) => {
	const [activeTab, setActiveTab] = useState("audience");
	const [audienceData, setAudienceData] = useState(null);
	const [domainData, setDomainData] = useState(null);
	const [appData, setAppData] = useState(null);
	const [loading, setLoading] = useState({
		audience: false,
		domains: false,
		apps: false,
		deals: false,
	});
	const [errors, setErrors] = useState({
		audience: null,
		domains: null,
		apps: null,
		deals: null,
	});

	// Helper functions for formatting
	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
			if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
			if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
			return value.toLocaleString();
		}
		if (typeof value === "string" && !isNaN(value)) {
			const num = parseInt(value);
			if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
			if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
			if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
			return num.toLocaleString();
		}
		return value;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") return `${value.toFixed(4)}%`;
		if (typeof value === "string" && !isNaN(value))
			return `${parseFloat(value).toFixed(4)}%`;
		return value;
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
			if (value >= 1000) return `$${(value / 1000).toFixed(2)}K`;
			if (value < 1) return `$${value.toFixed(4)}`;
			return `$${value.toFixed(2)}`;
		}
		return value;
	};

	const formatDate = (dateArray) => {
		if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3)
			return "N/A";
		try {
			const [year, month, day] = dateArray;
			const date = new Date(year, month - 1, day);
			return date.toLocaleDateString();
		} catch (error) {
			return "N/A";
		}
	};

	const formatDateTime = (dateArray) => {
		if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 6)
			return "N/A";
		try {
			const [year, month, day, hour, minute, second] = dateArray;
			const date = new Date(year, month - 1, day, hour, minute, second);
			return date.toLocaleString();
		} catch (error) {
			return "N/A";
		}
	};

	// Extract IDs from package data
	const getTargetingIds = (type) => {
		if (!packageData) return [];

		const packageVector = packageData.packageVector || {};

		switch (type) {
			case "audience":
				return packageVector.audienceId ? [packageVector.audienceId] : [];
			case "domains":
				return packageVector.targetedUrls
					? packageVector.targetedUrls.split(",").map((url) => `${url.trim()}`)
					: [];
			case "app":
				return packageVector.targetedUrls
					? packageVector.targetedUrls.split(",").map((url) => `${url.trim()}`)
					: packageVector.appBundleIds
						? packageVector.appBundleIds
								.split(",")
								.map((url) => `${url.trim()}`)
						: [];
			default:
				return [];
		}
	};

	// Generic API call function using packageApiService authentication
	const fetchTargetingData = async (type) => {
		const endpoints = {
			audience: "/audience/searchByIds",
			domains: "/domains/search-by-ids",
			app: "/app-data/search-by-ids",
		};

		const ids = getTargetingIds(type);

		if (!ids.length) {
			setErrors((prev) => ({
				...prev,
				[type]: `No ${type} IDs found in package data`,
			}));
			return;
		}

		setLoading((prev) => ({ ...prev, [type]: true }));
		setErrors((prev) => ({ ...prev, [type]: null }));

		try {
			// Check if packageApiService is available
			if (!window.packageApiService) {
				throw new Error("Package API service not available");
			}

			// Check authentication
			if (!window.packageApiService.isAuthenticated()) {
				throw new Error("Authentication required");
			}

			// Create different payload structures based on type
			let payload;

			switch (type) {
				case "audience":
					// Audience uses the original structure (array of IDs)
					payload = ids;
					break;

				case "domains":
					// Domains use domainIds field with noOfDays parameter
					payload = ids;
					break;

				case "app":
					// Apps use appBundleIds field with noOfDays parameter
					payload = ids;
					break;

				default:
					payload = ids;
			}

			// Use the packageApiService makeRequest method
			const response = await window.packageApiService.makeRequest(
				endpoints[type],
				{
					method: "POST",
					body: JSON.stringify(payload),
				},
			);

			if (!response.success) {
				throw new Error(response.error || `Failed to fetch ${type} data`);
			}

			const data = response.data;

			switch (type) {
				case "audience":
					setAudienceData(data);
					break;
				case "domains":
					setDomainData(data);
					break;
				case "app":
					setAppData(data);
					break;
			}
		} catch (error) {
			console.error(`Error fetching ${type} data:`, error);
			setErrors((prev) => ({ ...prev, [type]: error.message }));
		} finally {
			setLoading((prev) => ({ ...prev, [type]: false }));
		}
	};

	// Load data when tab changes
	useEffect(() => {
		if (!isOpen) return;

		switch (activeTab) {
			case "audience":
				if (!audienceData && !loading.audience) {
					fetchTargetingData("audience");
				}
				break;
			case "domains":
				if (!domainData && !loading.domains) {
					fetchTargetingData("domains");
				}
				break;
			case "app":
				if (!appData && !loading.apps) {
					fetchTargetingData("app");
				}
				break;
			case "deals":
				// Deals data is already available in packageData, no API call needed
				break;
		}
	}, [activeTab, isOpen]);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setActiveTab("audience");
			setAudienceData(null);
			setDomainData(null);
			setAppData(null);
			setErrors({ audience: null, domains: null, apps: null, deals: null });
			setLoading({
				audience: false,
				domains: false,
				apps: false,
				deals: false,
			});
		}
	}, [isOpen]);

	const renderTabContent = () => {
		const currentLoading = loading[activeTab];
		const currentError = errors[activeTab];
		let currentData;

		switch (activeTab) {
			case "audience":
				currentData = audienceData;
				break;
			case "domains":
				currentData = domainData;
				break;
			case "app":
				currentData = appData;
				break;
			case "deals":
				return renderDealsContent(packageData);
		}

		if (currentLoading) {
			return (
				<div className="tab-loading">
					<Loader2 className="animate-spin" size={32} />
					<p>Loading {activeTab} data...</p>
				</div>
			);
		}

		if (currentError) {
			return (
				<div className="tab-error">
					<AlertCircle size={32} className="error-icon" />
					<h4>
						Error Loading{" "}
						{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Data
					</h4>
					<p>{currentError}</p>
					<button
						onClick={() => fetchTargetingData(activeTab)}
						className="retry-button"
					>
						Retry
					</button>
				</div>
			);
		}

		if (!currentData) {
			return (
				<div className="tab-empty">
					<p>No {activeTab} data available</p>
				</div>
			);
		}

		// Render different content based on tab
		switch (activeTab) {
			case "audience":
				return renderAudienceContent(currentData);
			case "domains":
				return renderDomainsContent(currentData);
			case "app":
				return renderAppsContent(currentData);
			default:
				return null;
		}
	};

	const renderDealsContent = (data) => {
		if (!data || !data.packageVector) {
			return (
				<div className="tab-empty">
					<FileText size={32} className="empty-icon" />
					<p>No deal data found for this package</p>
				</div>
			);
		}

		const packageVector = data.packageVector;
		const packageMetrics = data.packageMetrics || {};

		// Get all deal IDs - either from the aggregated dealIds array or single dealId
		const dealIds = data.dealIds || [packageVector.dealId];
		const dealCount = dealIds.filter(Boolean).length;

		// Create array of deal rows combining dealSpecificInfo with dealMetrics
		const dealRows =
			data.dealSpecificInfo && data.dealSpecificInfo.length > 0
				? data.dealSpecificInfo.map((dealInfo, index) => {
						// Get corresponding deal metrics if available
						const dealMetric =
							data.dealMetrics && data.dealMetrics[index]
								? data.dealMetrics[index].metrics
								: {};

						return {
							...dealInfo,
							dealNumber: index + 1,
							// Include deal-level metrics
							metrics: dealMetric,
							// Include common package properties that don't vary by deal
							primaryVertical: packageVector.primaryVertical,
							deviceTypes: packageVector.deviceTypes,
							adPlacement: packageVector.adPlacement,
							distributionChannel: packageVector.distributionChannel,
							country: packageVector.country,
							createdDate: packageVector.createdDate,
							modifiedDate: packageVector.modifiedDate,
						};
					})
				: dealIds.filter(Boolean).map((dealId, index) => ({
						dealId: dealId,
						dealNumber: index + 1,
						// Fallback: use aggregated metrics divided by deal count (not ideal, but better than nothing)
						metrics: packageMetrics,
						// Fallback to using packageVector data for all deals if dealSpecificInfo not available
						...packageVector,
					}));

		// Check if this package has multiple deals
		const hasMultipleDeals = dealCount > 1;

		return (
			<div className="deals-content">
				<div className="deals-stats">
					<div className="stat-card">
						<h4>Total Deals</h4>
						<span className="stat-number">{dealCount}</span>
					</div>
				</div>

				<div className="table-container">
					<table className="data-table">
						<thead>
							<tr>
								<th>#</th>
								<th>Deal ID</th>
								<th>Deal Name</th>
								<th>Status</th>
								<th>Deal Price</th>
								<th>Rate Card CPM</th>
								{/* Deal Metrics Columns */}
								<th>Impressions</th>
								<th>Fills</th>
								<th>Bid CPM</th>
								<th>Bid Guidance</th>
								<th>Bid Spend</th>
								<th>Total Spend (USD)</th>
								<th>CTR (%)</th>
								<th>Viewability (%)</th>
								<th>Win Rate (%)</th>
								{/* Other Deal Info */}
								<th>Deal Source</th>
								<th>Primary Vertical</th>
								<th>Device Types</th>
								<th>Ad Placement</th>
								<th>Distribution Channel</th>
								<th>Country</th>
								<th>Deal Start Date</th>
								<th>Deal End Date</th>
								<th>Created Date</th>
								<th>Modified Date</th>
							</tr>
						</thead>
						<tbody>
							{dealRows.map((deal, index) => (
								<tr key={`${deal.dealId}-${index}`} className="deal-row">
									<td className="text-center">
										<span className="deal-number">{deal.dealNumber}</span>
									</td>
									<td className="text-left">
										<div className="deal-id-cell">
											<span className="deal-id-code">{deal.dealId}</span>
										</div>
									</td>
									<td className="text-left">
										<div className="deal-name-cell">
											<div className="deal-name">{deal.dealName || "N/A"}</div>
										</div>
									</td>
									<td>
										<span
											className={`status-badge ${deal.dealStatus?.toLowerCase() || "unknown"}`}
										>
											{deal.dealStatus || "Unknown"}
										</span>
									</td>
									<td className="text-right">
										{formatCurrency(deal.dealPrice)}
									</td>
									<td className="text-right">
										{formatCurrency(deal.rateCardCpm)}
									</td>

									{/* Deal Metrics */}
									<td className="text-right metric-cell">
										{formatNumber(deal.metrics?.impressions)}
									</td>
									<td className="text-right metric-cell">
										{formatNumber(deal.metrics?.fills)}
									</td>
									<td className="text-right metric-cell">
										{formatCurrency((deal.metrics?.bidCpm || 0) / 1000000)}
									</td>
									<td className="text-right metric-cell">
										{formatCurrency((deal.metrics?.bidGuidance || 0) / 1000000)}
									</td>
									<td className="text-right metric-cell">
										{formatCurrency((deal.metrics?.bidSpend || 0) / 1000000)}
									</td>
									<td className="text-right metric-cell">
										{formatCurrency(
											(deal.metrics?.totalSpendInUSD || 0) / 1000000,
										)}
									</td>
									<td className="text-right metric-cell">
										{formatPercentage(deal.metrics?.clickThroughRate)}
									</td>
									<td className="text-right metric-cell">
										{formatPercentage(deal.metrics?.viewability)}
									</td>
									<td className="text-right metric-cell">
										{formatPercentage(deal.metrics?.winRate)}
									</td>

									{/* Other Deal Info */}
									<td>{deal.dealSource || "N/A"}</td>
									<td>{deal.primaryVertical || "N/A"}</td>
									<td>
										<div className="device-types-cell">
											{deal.deviceTypes
												? deal.deviceTypes.split(",").map((device, i) => (
														<span key={i} className="device-tag">
															{device.trim()}
														</span>
													))
												: "N/A"}
										</div>
									</td>
									<td>{deal.adPlacement || "N/A"}</td>
									<td>{deal.distributionChannel || "N/A"}</td>
									<td>{deal.country?.toUpperCase() || "N/A"}</td>
									<td className="text-right">
										{formatDate(deal.dealStartDate)}
									</td>
									<td className="text-right">{formatDate(deal.dealEndDate)}</td>
									<td className="text-right">
										{formatDateTime(deal.createdDate)}
									</td>
									<td className="text-right">
										{formatDateTime(deal.modifiedDate)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	const renderAudienceContent = (data) => {
		// Handle the API response structure
		let audienceData = [];
		let totalCount = 0;

		// Check if data has the response structure with body
		if (data && data.body && data.body.data && Array.isArray(data.body.data)) {
			audienceData = data.body.data;
			totalCount =
				data.body.totalRecords || data.body.totalCount || data.body.data.length;
		}
		// Check if data has the direct structure
		else if (data && data.data && Array.isArray(data.data)) {
			audienceData = data.data;
			totalCount = data.totalRecords || data.totalCount || data.data.length;
		}
		// Check if data is directly an array
		else if (Array.isArray(data)) {
			audienceData = data;
			totalCount = data.length;
		}

		return (
			<div className="audience-content">
				<div className="audience-stats">
					<div className="stat-card">
						<h4>Total Audiences</h4>
						<span className="stat-number">{totalCount}</span>
					</div>
				</div>

				<div className="table-container">
					<table className="data-table">
						<thead>
							<tr>
								<th>Audience Name</th>
								<th>Account</th>
								<th>Type</th>
								<th>Data Source</th>
								<th>Region</th>
								<th>Status</th>
								<th>Primary Persona</th>
								<th>Strategy</th>
							</tr>
						</thead>
						<tbody>
							{audienceData.map((audience, index) => {
								// Access properties directly from audience object since they're at root level
								return (
									<tr key={audience.audienceId || index}>
										<td className="text-left">
											<div className="audience-name-cell">
												<div className="audience-name">
													{audience.cleanAudienceName ||
														audience.audienceName ||
														"N/A"}
												</div>
												{audience.notesDescription && (
													<div className="audience-description">
														{audience.notesDescription}
													</div>
												)}
											</div>
										</td>
										<td>{audience.accountName || "N/A"}</td>
										<td>
											<span className="type-badge">
												{audience.type || "N/A"}
											</span>
										</td>
										<td>{audience.dataSource || "N/A"}</td>
										<td>{audience.region || "N/A"}</td>
										<td>
											<span
												className={`status-badge ${audience.status?.toLowerCase() || "unknown"}`}
											>
												{audience.status || "Unknown"}
											</span>
										</td>
										<td>{audience.primaryPersona || "N/A"}</td>
										<td>
											{audience.audienceStrategy || audience.strategy || "N/A"}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	const renderDomainsContent = (data) => {
		// Handle the API response structure
		let domainData = [];
		let totalCount = 0;

		// Check if data has the direct structure with 'data' array (your actual structure)
		if (data && data.data && Array.isArray(data.data)) {
			domainData = data.data;
			totalCount = data.totalRecords || data.data.length;
		}
		// Check if data has the response structure with body
		else if (
			data &&
			data.body &&
			data.body.domain &&
			Array.isArray(data.body.domain)
		) {
			domainData = data.body.domain;
			totalCount = data.body.totalRecords || data.body.domain.length;
		}
		// Check if data has the alternative structure
		else if (data && data.domain && Array.isArray(data.domain)) {
			domainData = data.domain;
			totalCount = data.totalRecords || data.domain.length;
		}
		// Check if data is directly an array
		else if (Array.isArray(data)) {
			domainData = data;
			totalCount = data.length;
		}

		if (!domainData || domainData.length === 0) {
			return (
				<div className="tab-empty">
					<Globe size={32} className="empty-icon" />
					<p>No domain data found for this package</p>
				</div>
			);
		}

		return (
			<div className="domains-content">
				<div className="domains-stats">
					<div className="stat-card">
						<h4>Total Domains</h4>
						<span className="stat-number">{totalCount}</span>
					</div>
				</div>

				<div className="table-container">
					<table className="data-table">
						<thead>
							<tr>
								<th>Domain Name</th>
								{/* <th>Composite score</th>
                <th>Cost Performance score</th>
                <th>Performance score</th> */}
								<th>Categories</th>
								<th>Description</th>
								<th>Intended Audience</th>
								<th>Content Styles</th>
								<th>Safety Tier</th>
								{/* <th>Domain Reach</th>
                <th>Total Requests</th>
                <th>Total Impressions</th>
                <th>Total Clicks</th>
                <th>CTR (%)</th>
                <th>Viewability (%)</th>
                <th>Win Rate (%)</th>
                <th>Revenue/Impression</th>
                <th>Cost/Impression</th>
                <th>Total Spend (USD)</th> */}
							</tr>
						</thead>
						<tbody>
							{domainData.map((domain, index) => (
								<tr key={domain.id || index}>
									<td className="text-left">
										<div className="domain-name-cell">
											<div className="domain-name">
												{domain.domainName || "N/A"}
											</div>
										</div>
									</td>
									{/* <td className="text-right">{formatNumber(domain.compositeScore)}</td>
                  <td className="text-right">{formatNumber(domain.costPerfomanceScore)}</td>
                  <td className="text-right">{formatNumber(domain.perfomanceScore)}</td> */}
									<td className="text-left">
										<div className="categories-cell">
											{domain.categories && domain.categories.length > 0 ? (
												<div className="category-tags">
													{domain.categories
														.filter((cat) => cat && cat.trim())
														.slice(0, 3)
														.map((category, idx) => (
															<span key={idx} className="category-tag">
																{category}
															</span>
														))}
													{domain.categories.filter((cat) => cat && cat.trim())
														.length > 3 && (
														<span className="category-more">
															+
															{domain.categories.filter(
																(cat) => cat && cat.trim(),
															).length - 3}{" "}
															more
														</span>
													)}
												</div>
											) : (
												"N/A"
											)}
										</div>
									</td>
									<td className="text-left description-cell">
										<div
											className="description-text"
											title={domain.description || "N/A"}
										>
											{domain.description && domain.description.length > 100
												? `${domain.description.substring(0, 100)}...`
												: domain.description || "N/A"}
										</div>
									</td>
									<td className="text-left audience-cell">
										<div
											className="audience-text"
											title={domain.intendedAudience || "N/A"}
										>
											{domain.intendedAudience &&
											domain.intendedAudience.length > 80
												? `${domain.intendedAudience.substring(0, 80)}...`
												: domain.intendedAudience || "N/A"}
										</div>
									</td>
									<td className="text-left">
										{domain.contentStyles && domain.contentStyles.length > 0 ? (
											<div className="content-styles">
												{domain.contentStyles.slice(0, 2).map((style, idx) => (
													<span key={idx} className="style-tag">
														{style}
													</span>
												))}
												{domain.contentStyles.length > 2 && (
													<span className="style-more">
														+{domain.contentStyles.length - 2}
													</span>
												)}
											</div>
										) : (
											"N/A"
										)}
									</td>
									<td>
										<span
											className={`safety-badge ${(domain.safetyTier || "unknown").toLowerCase()}`}
										>
											{domain.safetyTier || "Unknown"}
										</span>
									</td>
									{/* <td className="text-right">{formatNumber(domain.domainReach)}</td>
                  <td className="text-right">{formatNumber(domain.totalCprMarketRequests)}</td>
                  <td className="text-right">{formatNumber(domain.totalImpressions)}</td>
                  <td className="text-right">{formatNumber(domain.totalCprClicks)}</td>
                  <td className="text-right">{formatPercentage(domain.calculated_ctr)}</td>
                  <td className="text-right">{formatPercentage(domain.avgViewability)}</td>
                  <td className="text-right">{formatPercentage(domain.avgOxWinRate)}</td>
                  <td className="text-right">{formatCurrency(domain.calculated_revenueperimpression)}</td>
                  <td className="text-right">{formatCurrency(domain.calculated_costperimpression)}</td>
                  <td className="text-right">{formatCurrency(domain.totalCprMarketDemandPartnerSpendInUsd / 1000000)}</td> */}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	const renderAppsContent = (data) => {
		// Handle the API response structure
		let appData = [];
		let totalCount = 0;

		// Check if data has the direct structure with 'data' array (your actual structure)
		if (data && data.data && Array.isArray(data.data)) {
			appData = data.data;
			totalCount = data.totalRecords || data.data.length;
		}
		// Check if data has the response structure with body
		else if (
			data &&
			data.body &&
			data.body.apps &&
			Array.isArray(data.body.apps)
		) {
			appData = data.body.apps;
			totalCount = data.body.totalRecords || data.body.apps.length;
		}
		// Check if data has the alternative structure
		else if (data && data.apps && Array.isArray(data.apps)) {
			appData = data.apps;
			totalCount = data.totalRecords || data.apps.length;
		}
		// Check if data is directly an array
		else if (Array.isArray(data)) {
			appData = data;
			totalCount = data.length;
		}

		if (!appData || appData.length === 0) {
			return (
				<div className="tab-empty">
					<Smartphone size={32} className="empty-icon" />
					<p>No app data found for this package</p>
				</div>
			);
		}

		// Helper function to parse JSON strings safely
		const parseJsonField = (field) => {
			if (!field) return [];
			if (Array.isArray(field)) return field;
			if (typeof field === "string") {
				try {
					return JSON.parse(field);
				} catch {
					return field.split(",").map((item) => item.trim());
				}
			}
			return [];
		};

		return (
			<div className="apps-content">
				<div className="apps-stats">
					<div className="stat-card">
						<h4>Total Apps</h4>
						<span className="stat-number">{totalCount}</span>
					</div>
				</div>

				<div className="table-container">
					<table className="data-table">
						<thead>
							<tr>
								<th>App Name</th>
								<th>Bundle ID</th>
								<th>Category</th>
								<th>Subcategory</th>
								<th>Description</th>
								<th>Developer</th>
								<th>Platform</th>
								<th>Rating</th>
								<th>Content Rating</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{appData.map((app, index) => {
								const subcategories = parseJsonField(app.subcategory);
								const features = parseJsonField(app.features);

								return (
									<tr key={app.appId || app.bundleId || index}>
										<td className="text-left">
											<div className="app-name-cell">
												<div className="app-name">
													{app.appName || app.appinfoName || "N/A"}
												</div>
												{app.shortDescription && (
													<div className="app-short-description">
														{app.shortDescription}
													</div>
												)}
											</div>
										</td>
										<td className="text-left">
											<span className="bundle-id">{app.bundleId || "N/A"}</span>
										</td>
										<td className="text-left">
											<span className="category-badge">
												{app.category || "N/A"}
											</span>
										</td>
										<td className="text-left">
											<div className="subcategories-cell">
												{subcategories && subcategories.length > 0 ? (
													<div className="subcategory-tags">
														{subcategories.slice(0, 2).map((subcat, idx) => (
															<span key={idx} className="subcategory-tag">
																{subcat}
															</span>
														))}
														{subcategories.length > 2 && (
															<span className="subcategory-more">
																+{subcategories.length - 2} more
															</span>
														)}
													</div>
												) : (
													"N/A"
												)}
											</div>
										</td>
										<td className="text-left description-cell">
											<div
												className="description-text"
												title={app.description || "N/A"}
											>
												{app.description && app.description.length > 100
													? `${app.description.substring(0, 100)}...`
													: app.description || "N/A"}
											</div>
										</td>
										<td className="text-left">{app.developer || "N/A"}</td>
										<td>
											<span className="platform-badge">
												{app.appinfoPlatform || app.marketSource || "Unknown"}
											</span>
										</td>
										<td className="text-right">
											<div className="rating-cell">
												<div className="rating-value">
													{app.rating ? app.rating.toFixed(1) : "N/A"}
												</div>
												{app.ratingCount && (
													<div className="rating-count">
														({formatNumber(app.ratingCount)})
													</div>
												)}
											</div>
										</td>
										<td>
											<span className="content-rating-badge">
												{app.contentRating || app.appinfoContentRating || "N/A"}
											</span>
										</td>
										<td>
											<span
												className={`status-badge ${(app.marketStatus || "unknown").toLowerCase()}`}
											>
												{app.marketStatus || "Unknown"}
											</span>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	if (!isOpen) return null;

	const tabs = [
		{ id: "audience", label: "Audience", icon: Users },
		{ id: "domains", label: "Domains", icon: Globe },
		{ id: "app", label: "App", icon: Smartphone },
		{ id: "deals", label: "Deals", icon: FileText },
	];

	return (
		<div className="modal-overlay">
			<div className="modal-container">
				<div className="modal-header">
					<h3 className="modal-title">
						Targeting Details -{" "}
						{packageData?.packageVector?.packageName || "Package"}
					</h3>
					<button onClick={onClose} className="close-button">
						<X size={20} />
					</button>
				</div>

				<div className="modal-tabs">
					{tabs.map(({ id, label, icon: Icon }) => (
						<button
							key={id}
							onClick={() => setActiveTab(id)}
							className={`tab-button ${activeTab === id ? "active" : ""}`}
						>
							<Icon size={16} />
							{label}
							{loading[id] && (
								<Loader2 size={12} className="animate-spin tab-spinner" />
							)}
						</button>
					))}
				</div>

				<div className="modal-content">{renderTabContent()}</div>
			</div>

			<style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          width: 95vw;
          max-width: 1200px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .modal-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .close-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          color: #6b7280;
          transition: all 0.2s;
        }

        .close-button:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-tabs {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .tab-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px 20px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          transition: all 0.2s;
          position: relative;
        }

        .tab-button:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .tab-button.active {
          color: #0076BD;
          background: white;
          border-bottom: 2px solid #0076BD;
        }

        .tab-spinner {
          margin-left: 4px;
        }

        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .tab-loading, .tab-error, .tab-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .tab-error {
          color: #ef4444;
        }

        .error-icon {
          color: #ef4444;
          margin-bottom: 12px;
        }

        .empty-icon {
          color: #9ca3af;
          margin-bottom: 12px;
        }

        .retry-button {
          margin-top: 12px;
          padding: 8px 16px;
          background: #0076BD;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .retry-button:hover {
          background: #005a94;
        }

        

        .audience-stats, .domains-stats, .apps-stats, .deals-stats {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
          flex: 1;
        }

        .stat-card h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
        }

        .stat-number {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
        }

        .deal-status {
          font-size: 18px;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 20px;
          text-transform: uppercase;
        }

        .deal-status.active {
          background: #dcfce7;
          color: #166534;
        }

        .deal-status.paused {
          background: #fef3c7;
          color: #92400e;
        }

        .deal-status.inactive {
          background: #fecaca;
          color: #991b1b;
        }

        .deal-status.unknown {
          background: #f1f5f9;
          color: #475569;
        }

        .app-name-cell {
  max-width: 200px;
}

.app-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  word-wrap: break-word;
}

.app-short-description {
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  margin: 0;
}

.bundle-id {
  font-family: monospace;
  font-size: 11px;
  background: #f1f5f9;
  padding: 2px 4px;
  border-radius: 3px;
  color: #475569;
}

.category-badge {
  background: #e0f2fe;
  color: #0277bd;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
}

.subcategories-cell {
  max-width: 120px;
}

.subcategory-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.subcategory-tag {
  display: inline-block;
  background: #f0f9ff;
  color: #0369a1;
  padding: 1px 4px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  white-space: nowrap;
}

.subcategory-more {
  display: inline-block;
  color: #6b7280;
  font-size: 9px;
  font-weight: 500;
  font-style: italic;
}

.platform-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
}

.rating-cell {
  text-align: right;
}

.rating-value {
  font-weight: 600;
  color: #1e293b;
}

.rating-count {
  font-size: 10px;
  color: #6b7280;
}

.content-rating-badge {
  background: #fef3c7;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
}

        /* Table Styles */
        .table-container {
          overflow-x: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          margin-bottom: 24px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          min-width: 1400px;
        }

        .data-table th {
          background: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          color: #475569;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .data-table td {
          padding: 10px 8px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .data-table .text-right {
          text-align: right;
        }

        .data-table .text-left {
          text-align: left;
        }

        .deal-name-cell, .audience-name-cell {
          max-width: 200px;
        }

        .deal-name, .audience-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
          word-wrap: break-word;
        }

        .deal-description, .audience-description {
          font-size: 11px;
          color: #64748b;
          line-height: 1.3;
          margin: 0;
        }

        .category-cell {
          max-width: 150px;
        }

        .sub-category {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }

        .device-types-cell {
          max-width: 120px;
        }

        .device-tag {
          display: inline-block;
          background: #e0f2fe;
          color: #0277bd;
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          margin: 1px;
        }

        .type-badge {
          background: #dbeafe;
          color: #1e40af;
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-badge {
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-badge.processed, .status-badge.active {
          background: #dcfce7;
          color: #166534;
        }

        .status-badge.processing, .status-badge.paused {
          background: #fef3c7;
          color: #92400e;
        }

        .status-badge.failed, .status-badge.inactive {
          background: #fecaca;
          color: #991b1b;
        }

        .status-badge.expired {
          background: #f1f5f9;
          color: #475569;
        }

        .boolean-badge {
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .boolean-badge.true {
          background: #dcfce7;
          color: #166534;
        }

        .boolean-badge.false {
          background: #f1f5f9;
          color: #475569;
        }

        /* Multi-deal notice */
.multi-deal-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #e0f2fe;
  border: 1px solid #0ea5e9;
  border-radius: 6px;
  color: #0c4a6e;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 20px;
}

/* Aggregated stats card styling */
.stat-card.aggregated-notice {
  background: #fef3c7;
  border-color: #fbbf24;
}

.stat-card.aggregated-notice h4 {
  color: #92400e;
}

.stat-card.aggregated-notice .stat-number {
  color: #92400e;
  font-size: 16px;
}

/* Deal row styling */
.deal-row {
  transition: background-color 0.2s;
}

.deal-row:hover {
  background-color: #f8fafc;
}

.deal-row:nth-child(even) {
  background-color: #fafafa;
}

.deal-row:nth-child(even):hover {
  background-color: #f1f5f9;
}

/* Deal number badge */
.deal-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e0f2fe;
  color: #0369a1;
  font-weight: 700;
  font-size: 12px;
  border-radius: 50%;
}

/* Deal ID cell with inline copy button */
.deal-id-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.deal-id-code {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  background: #f1f5f9;
  padding: 3px 6px;
  border-radius: 3px;
}

.copy-inline-btn {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  transition: all 0.2s;
  flex-shrink: 0;
  line-height: 1;
  opacity: 0.7;
}

.copy-inline-btn:hover {
  background: #0076BD;
  border-color: #0076BD;
  opacity: 1;
  transform: scale(1.1);
}

.deal-row:hover .copy-inline-btn {
  opacity: 1;
}

/* Metrics notice at bottom */
.metrics-notice {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 20px;
  margin-top: 24px;
}

.metrics-notice h4 {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: #166534;
}

.metrics-notice p {
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #15803d;
  line-height: 1.5;
}

.aggregated-metrics-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: white;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
}

.summary-label {
  font-size: 12px;
  font-weight: 500;
  color: #166534;
}

.summary-value {
  font-size: 14px;
  font-weight: 700;
  color: #15803d;
}

/* Update table to accommodate more columns */
.data-table {
  min-width: 1800px;
}

.text-center {
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .multi-deal-notice {
    padding: 10px 12px;
    font-size: 12px;
  }

  .aggregated-metrics-summary {
    grid-template-columns: 1fr;
  }

  .deal-number {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
}

        /* Deal Details Section */
        .deal-details-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
        }

        .deal-details-section h4 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
        }

        .deal-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .info-label {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
        }

        .info-value {
          font-size: 14px;
          color: #111827;
          word-wrap: break-word;
        }

        .audience-list, .domains-list, .apps-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .audience-item, .domain-item, .app-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.2s;
        }

        .audience-item:hover, .domain-item:hover, .app-item:hover {
          border-color: #0076BD;
          box-shadow: 0 2px 8px rgba(0, 118, 189, 0.1);
        }

        .audience-header, .domain-header, .app-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .audience-header h5, .domain-header h5, .app-header h5 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }

        .audience-type, .domain-category, .app-platform {
          background: #e0f2fe;
          color: #0277bd;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .metric {
          display: flex;
          gap: 4px;
          align-items: center;
        }

        .metric-label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .metric-value {
          font-size: 12px;
          color: #111827;
          font-weight: 600;
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .modal-container {
            width: 95vw;
            max-height: 90vh;
          }

          .modal-header {
            padding: 16px 20px;
          }

          .modal-title {
            font-size: 16px;
          }

          .tab-button {
            padding: 12px 16px;
            font-size: 13px;
          }

          .modal-content {
            padding: 20px;
          }

          .audience-stats, .domains-stats, .apps-stats, .deals-stats {
            flex-direction: column;
          }

          .audience-header, .domain-header, .app-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .audience-metrics, .domain-metrics, .app-metrics {
            gap: 12px;
          }

          .deal-info-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
		</div>
	);
};

export default TargetingModal;
